import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Settings,
  LogOut,
  Copy,
  ChevronDown,
  Newspaper,
  Store,
  ArrowLeftRight,
  ClipboardList,
  Network,
  ArrowLeft,
  BarChart3,
  FileStack,
  GitBranch,
  Briefcase,
  Search,
  ShieldAlert,
  Telescope,
  AlertTriangle,
} from 'lucide-react';
import {
  Sidebar,
  SidebarMenu,
  useSidebar,
} from '@/components/ui/sidebar';
import { Badge } from '@/components/ui/badge';
import { SafeImageFallback } from '@/components/ui/SafeImageFallback';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useVendor } from '@/contexts/VendorContext';
import { useInsightsPrefetch } from '@/hooks/useInsightsPrefetch';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { useToast } from '@/hooks/use-toast';
import { useAppShellSummary } from '@/hooks/useAppShellSummary';
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile';
import { useBreachAcknowledgementCount } from '@/hooks/useBreachAcknowledgementCount';
import { appBuildInfo, formatBuildInfoForClipboard, formatBuiltAtForDisplay, formatVersionForDisplay } from '@/lib/buildInfo';
import {
  shouldShowAdminSidebarDashboard,
  shouldShowAdminSidebarSection,
} from '@/lib/adminNavigationAccess';
import { getLastAdminDashboardRoute } from '@/lib/adminNavigationState';
import { beginPortalSwitch } from '@/lib/portalSwitch';
import {
  ADMIN_PROFILE_LABEL,
  CUSTOMER_PROFILE_LABEL,
  MSP_PROFILE_LABEL,
  SURFACE_PROFILE_LABEL,
  SUPPLIER_PROFILE_LABEL,
  SWITCH_PROFILE_LABEL,
} from '@/lib/surfaceViewLabel';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { AppCommandPalette } from './AppCommandPalette';
import { getCommandPaletteItems, resolveCanManageOrgSso } from '@/lib/commandPaletteRoutes';
import { getAdminNavItems } from '@/lib/adminNavRegistry';
import { getSettingsShortcutHint } from '@/lib/settingsShortcut';
import { ThemeModeMenu } from '@/components/theme/ThemeModeMenu';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { CUSTOMER_FEATURE_KEYS } from '@/lib/customerFeaturesCatalog';
import { FEATURE_FLAG_KEYS } from '@/lib/featureFlagsCatalog';
import { formatClipboardJson } from '@/lib/clipboardJson';
import { getProfileDisplayName, getProfileInitials, hasProfileDisplayIdentity } from '@/lib/profileDisplay';
import { initialsFromName } from '@/components/ui/data-table/data-table-utils';
import { getCanonicalOrganizationLogoDisplayUrl, useCanonicalOrganizationLogo } from '@/hooks/useCanonicalOrganizationLogo';
import {
  SurfaceSidebarBackdrop,
  SurfaceSidebarBrandButton,
  SurfaceSidebarBrandPanel,
  SurfaceSidebarCopyButton,
  SurfaceSidebarContent,
  SurfaceSidebarFooter,
  SurfaceSidebarHeader,
  SurfaceSidebarNavItemLink,
  SurfaceSidebarPortalLink,
  SurfaceSidebarProfileMenu,
  SurfaceSidebarSection,
  SurfaceSidebarSwitcherControl,
  SurfaceSidebarSwitcherButton,
} from './SurfaceSidebar';
import { AdvisoriesNavItem } from '@/components/advisories/AdvisoriesNavItem';

const mainNavItems = [
  { title: 'Navigator', url: '/navigator', icon: LayoutDashboard },
  { title: 'Insights', url: '/insights', icon: BarChart3 },
  { title: 'My Suppliers', url: '/suppliers', icon: Building2 },
   { title: 'Dependencies', url: '/dependencies', icon: GitBranch },
  { title: 'Marketplace', url: '/marketplace', icon: Store },
  { title: 'Breach Feed', url: '/breach-feed', icon: Newspaper, hiddenWhenManualBreachNotificationsEnabled: true },
  { title: 'Breach Centre', url: '/breach-notifications', icon: ShieldAlert, featureKey: FEATURE_FLAG_KEYS.manualBreachNotifications },
  { title: 'Actions', url: '/actions', icon: ClipboardList },
  { title: 'TeleLens', url: '/telelens', icon: Telescope, featureKey: CUSTOMER_FEATURE_KEYS.telelens },
  { title: 'Reports', url: '/reports', icon: FileStack },
];

const mspNavItems = [
  { title: 'Customers', url: '/customers', icon: Network },
  { title: 'MSP Insights', url: '/msp-insights', icon: BarChart3 },
  { title: 'Heatmap', url: '/msp-heatmap', icon: Search, featureKey: FEATURE_FLAG_KEYS.mspHeatmap },
  { title: 'Templates', url: '/msp-templates', icon: FileStack },
  { title: 'Pitch Book', url: '/pitch-book', icon: Briefcase, featureKey: CUSTOMER_FEATURE_KEYS.mspPitchBook },
];

// customer_service members see only their allocated customers and Pitch Book
// (ENT-5614). This list is UX only: the backend refuses them the MSP's own org
// data (ENT-5616: internal_security.is_org_member excludes customer_service, the
// read-model RPCs raise 42501, and the Edge read models return 403
// customer_service_own_org_forbidden), while has_msp_access serves their
// allocated customers and is_msp_member serves Pitch Book.
const customerServiceMspNavUrls = new Set(['/customers', '/pitch-book']);

const ORGANIZATION_SWITCHER_PAGE_SIZE = 8;

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const navigate = useNavigate();
  const settingsShortcutHint = getSettingsShortcutHint();
  const { toast } = useToast();
  const {
    user,
    organization,
    organizations,
    switchOrganizationContext,
    returnToMspOrg,
    membership,
    isMspOrg,
    isMspContext,
    mspHomeOrg,
    mspCustomerOrgs,
  } = useOrganization();
  const { contextVendorProfile, contextClaimableProfiles, contextDomain } = useVendor();
  const { enabled: isMspHeatmapEnabled } = useFeatureAccess(FEATURE_FLAG_KEYS.mspHeatmap);
  const { enabled: isMspPitchBookEnabled } = useFeatureAccess(CUSTOMER_FEATURE_KEYS.mspPitchBook);
  const { enabled: isTeleLensEnabled } = useFeatureAccess(CUSTOMER_FEATURE_KEYS.telelens);
  const { enabled: isCustomQuestionnairePickerEnabled } = useFeatureAccess(FEATURE_FLAG_KEYS.customQuestionnairePicker, {
    source: 'beta_feature',
    enabled: Boolean(organization?.id),
  });
  const { enabled: isManualBreachNotificationsEnabled } = useFeatureAccess(FEATURE_FLAG_KEYS.manualBreachNotifications);
  const { enabled: isScanEngineSettingsEnabled } = useFeatureAccess(FEATURE_FLAG_KEYS.scanEngineSettings);
  const { enabled: isScanEngineOperationsEnabled } = useFeatureAccess(FEATURE_FLAG_KEYS.scanEngineOperations);
  // ENT-3705: drive the organisation-settings palette rows (Emergency access tab, API tokens tab).
  const { enabled: isBreakglassEnabled } = useFeatureAccess(FEATURE_FLAG_KEYS.breakglassAccess);
  // ENT-3705: match Settings.tsx exposure — the API tokens surface is shown when EITHER the
  // per-user `personal_access_tokens_ui` beta flag OR the `developer_integrations` entitlement is
  // held, so the palette row stays consistent with the tab for beta-enabled users.
  const { enabled: isDeveloperIntegrationsCustomerEnabled } = useFeatureAccess(CUSTOMER_FEATURE_KEYS.developerIntegrations);
  // ENT-4214: Enterprise SSO is an organisation rollout flag, not a customer entitlement.
  const { enabled: isSsoRolloutEnabled } = useFeatureAccess(FEATURE_FLAG_KEYS.enterpriseSso, {
    source: 'beta_feature',
    organizationId: organization?.id,
  });
  // ENT-5511: SupplySteward is a per-organisation switch Supply Guard controls.
  const { enabled: isSupplyStewardEnabled } = useFeatureAccess(FEATURE_FLAG_KEYS.supplySteward, {
    source: 'beta_feature',
    organizationId: organization?.id,
  });
  const { enabled: isPersonalAccessTokensUiEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.personalAccessTokensUi,
    { userId: user?.id },
  );
  const isDeveloperIntegrationsEnabled =
    isPersonalAccessTokensUiEnabled || isDeveloperIntegrationsCustomerEnabled;
  // ENT-5616: breach cases are the org's own data, which the backend refuses a customer_service
  // member on its own (MSP) org (403 customer_service_own_org_forbidden); do not ask for them.
  const isCustomerServiceOnOwnOrg = membership?.role === 'customer_service' && !isMspContext;
  const { count: breachAcknowledgementCount } = useBreachAcknowledgementCount({
    organizationId: organization?.id,
    enabled: isManualBreachNotificationsEnabled && !isCustomerServiceOnOwnOrg,
  });
  const isViewingAsCustomer = isMspContext;
  const mspOrg = mspHomeOrg;
  const isMSP = isMspOrg && !isViewingAsCustomer;
  const [organizationSwitcherOpen, setOrganizationSwitcherOpen] = useState(false);
  const [organizationSearchQuery, setOrganizationSearchQuery] = useState('');
  const [organizationPage, setOrganizationPage] = useState(1);
  const { prefetchInsights, prefetchSuppliers, prefetchPoam } = useInsightsPrefetch();
  const { currentUserProfile: shellProfile } = useAppShellSummary({
    organizationId: organization?.id,
    agentOrganizationId: mspOrg?.id,
    userId: user?.id,
    deferUntilIdle: true,
  });
  const directProfile = useCurrentUserProfile(user?.id);
  const resolvedProfile = hasProfileDisplayIdentity(directProfile) ? directProfile : shellProfile;

  // Map nav items to their prefetch functions
  const getPrefetchHandler = useCallback((url: string) => {
    switch (url) {
      case '/insights':
        return prefetchInsights;
      case '/suppliers':
        return prefetchSuppliers;
      case '/actions':
        return prefetchPoam;
      default:
        return undefined;
    }
  }, [prefetchInsights, prefetchSuppliers, prefetchPoam]);

  const handleSignOut = async () => {
    // Use centralized auth utility for consistent global session revocation
    const { signOutGlobally } = await import('@/lib/auth');
    await signOutGlobally('/', true);
  };

  const userInitials = getProfileInitials(resolvedProfile, user?.email);
  const activeWorkspaceCopyValue = organization ? `${organization.name} (${organization.id})` : null;
  const orgSettings = organization?.settings as { domain?: string } | null;
  // ENT-5657 A6 / ENT-2421: the workspace mark is exactly the server resolver result Settings uses
  // (organisation upload → the typed claim-linked listing's uploaded logo → initials). No favicon,
  // discovered logo or claimable-profile fallback; nothing renders until the active org resolves.
  const canonicalWorkspaceLogoQuery = useCanonicalOrganizationLogo(organization?.id);
  const canonicalWorkspaceLogo =
    organization?.id && canonicalWorkspaceLogoQuery.data?.organizationId === organization.id
      ? canonicalWorkspaceLogoQuery.data
      : null;
  const activeOrganizationImageSrc = getCanonicalOrganizationLogoDisplayUrl(
    canonicalWorkspaceLogo,
    organization?.updated_at ?? null,
  );
  // ENT-5657 (Codex review R2): a failed or malformed resolution is an
  // explicit error state, never a definitive initials identity.
  const workspaceMarkState: 'loading' | 'logo' | 'initials' | 'error' = canonicalWorkspaceLogo
    ? (activeOrganizationImageSrc ? 'logo' : 'initials')
    : !organization?.id
    ? 'initials'
    : canonicalWorkspaceLogoQuery.isError
    ? 'error'
    : 'loading';
  const workspaceInitials = initialsFromName(organization?.name ?? '');
  const activeProfileName = getProfileDisplayName(resolvedProfile, user?.email);
  const activeProfileCopyValue = activeProfileName && user?.id && user?.email
    ? `${activeProfileName} (${user.id}) - ${user.email}`
    : null;
  const isCustomerService = membership?.role === 'customer_service';
  const isSuperAdmin = useIsSuperAdmin(user?.id);
  const isAdmin = shouldShowAdminSidebarSection({
    organizationRole: membership?.role ?? 'user',
    isSuperAdmin,
  });
  const isSuperAdminSurfaceEnabled = shouldShowAdminSidebarDashboard({
    isSuperAdmin,
  });
  const adminDashboardUrl = getLastAdminDashboardRoute();
  // ENT-3705: the Admin group (Team · Design Studio · <Scope> Settings) is driven by the single
  // registry so the sidebar, command palette and settings pages never drift. Keep the Design Studio
  // predicate verbatim inside the registry (its correction is ENT-3721, out of scope here).
  const canManageOrgSettings =
    membership?.role === 'admin' || membership?.role === 'super_admin';
  // ENT-4073 R5: whether the viewer may actually MANAGE SSO — mirrors `org-sso-admin`'s single
  // authorization rule (a REAL, non-break-glass org admin, or a platform super admin), excluding the
  // MSP-virtual and break-glass admins that pass `canManageOrgSettings` but org-sso-admin rejects.
  // Pure helper so the membership-shape matrix is unit-tested without rendering the shell.
  const canManageOrgSso = resolveCanManageOrgSso(membership, isSuperAdmin);
  const adminNavItems = getAdminNavItems({
    isAdmin,
    canManageOrgSettings,
    canManageOrgSso,
    isCustomQuestionnairePickerEnabled,
    isViewingAsCustomer,
    isMSP,
    isViewingManagedCustomer: isViewingAsCustomer,
    isSupplierPortal: false,
    isBreakglassEnabled: false,
    isDeveloperIntegrationsEnabled: false,
    isEnterpriseSsoEnabled: false,
    isSupplyStewardEnabled: false,
  });
  const appVersionLabel = `Version ${formatVersionForDisplay(appBuildInfo.version)}`;
  const builtAtLabel = `Built ${formatBuiltAtForDisplay(appBuildInfo.builtAtUtc)}`;
  const hasWorkspaceSupplierContext = Boolean(contextVendorProfile)
    || Boolean(contextClaimableProfiles.length);
  const activeProfile = contextVendorProfile;
  const activeClaimable = contextClaimableProfiles;
  const effectiveContextDomain = hasWorkspaceSupplierContext
    ? (contextDomain || orgSettings?.domain?.toLowerCase() || organization?.slug?.toLowerCase())
    : null;
  const hasSupplierPortal = hasWorkspaceSupplierContext
    && Boolean(activeProfile || effectiveContextDomain || activeClaimable.length > 0);
  const supplierPortalUrl = hasSupplierPortal
    ? activeProfile
      ? '/vendor'
      : `/vendor/claim${effectiveContextDomain ? `?domain=${effectiveContextDomain}` : ''}`
    : null;
  const supplierPortalLabel = activeProfile
    ? SUPPLIER_PROFILE_LABEL
    : 'Claim Supplier Profile';
  // Supplier Profile is workspace-scoped. The switcher must not change currentOrgId; it only
  // opens the active workspace's supplier profile or active workspace claim flow.
  const enterSupplierPortal = useCallback(async () => {
    if (!supplierPortalUrl) {
      return;
    }
    navigate(supplierPortalUrl);
  }, [supplierPortalUrl, navigate]);
  const handleSupplierPortalClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      beginPortalSwitch('vendor');
      // Preserve open-in-new-tab / modified-click / non-primary-button behaviour.
      if (
        event.defaultPrevented
        || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
        || event.button !== 0
      ) {
        return;
      }
    },
    [],
  );
  const handleSupplierPortalCommand = useCallback(() => {
    beginPortalSwitch('vendor');
    return enterSupplierPortal();
  }, [enterSupplierPortal]);
  // ENT-3705: AppCommandPalette.handleSelect runs onSelect but never navigates for it, so the
  // MSP-return command must both switch back to the home org AND route to MSP Settings itself.
  const handleMspSettingsCommand = useCallback(async () => {
    await returnToMspOrg();
    navigate('/settings/organisation');
  }, [returnToMspOrg, navigate]);
  const commandPaletteItems = getCommandPaletteItems({
    isAdmin,
    canInviteMembers: membership?.role === 'admin',
    isSuperAdmin,
    isCustomerService,
    isMSP,
    isMspHeatmapEnabled,
    isMspPitchBookEnabled,
    isManualBreachNotificationsEnabled,
    isScanEngineSettingsEnabled,
    isScanEngineOperationsEnabled,
    isTeleLensEnabled,
    isCustomQuestionnairePickerEnabled,
    isViewingAsCustomer,
    hasSupplierPortal: !!(hasSupplierPortal && supplierPortalUrl),
    supplierPortalTitle: supplierPortalLabel,
    supplierPortalUrl: supplierPortalUrl ?? undefined,
    supplierPortalOnSelect: hasSupplierPortal && supplierPortalUrl ? handleSupplierPortalCommand : undefined,
    // ENT-3705: registry-driven organisation settings rows.
    canManageOrgSettings,
    // ENT-4073: real SSO-management authorization (mirrors org-sso-admin), gates the SSO setup action.
    canManageOrgSso,
    isViewingManagedCustomer: isViewingAsCustomer,
    isBreakglassEnabled,
    isDeveloperIntegrationsEnabled,
    activeOrgName: organization?.name ?? undefined,
    activeOrgId: organization?.id ?? undefined,
    isSsoRolloutEnabled,
    isSupplyStewardEnabled,
    mspOrgName: mspOrg?.name ?? undefined,
    mspSettingsOnSelect: isViewingAsCustomer ? handleMspSettingsCommand : undefined,
  });
  const contextualCommandPaletteItems = isViewingAsCustomer && mspOrg
    ? [
        {
          id: 'exit-customer-view',
          kind: 'action' as const,
          title: `Exit ${CUSTOMER_PROFILE_LABEL}`,
          subtitle: `Return to ${mspOrg.name}`,
          pathLabel: `Action > ${CUSTOMER_PROFILE_LABEL} > Exit ${CUSTOMER_PROFILE_LABEL}`,
          url: '/customers',
          icon: ArrowLeft,
          section: 'Actions' as const,
          keywords: ['return to msp', 'leave customer view', 'back to msp', 'exit customer'],
          navigateAfterSelect: true,
          onSelect: async () => {
            await returnToMspOrg();
            navigate('/customers');
          },
        },
      ]
    : [];
  const visibleMspNavItems = useMemo(
    () => mspNavItems.filter((item) => {
      if (!('featureKey' in item)) {
        return true;
      }

      if (item.featureKey === FEATURE_FLAG_KEYS.mspHeatmap) {
        return isMspHeatmapEnabled;
      }

      if (item.featureKey === CUSTOMER_FEATURE_KEYS.mspPitchBook) {
        return isMspPitchBookEnabled;
      }

      return true;
    }),
    [isMspHeatmapEnabled, isMspPitchBookEnabled],
  );
  const visibleMainNavItems = useMemo(
    () => mainNavItems.filter((item) => {
      if (
        'hiddenWhenManualBreachNotificationsEnabled' in item &&
        item.hiddenWhenManualBreachNotificationsEnabled &&
        isManualBreachNotificationsEnabled
      ) {
        return false;
      }

      if (!('featureKey' in item)) {
        return true;
      }

      if (item.featureKey === FEATURE_FLAG_KEYS.manualBreachNotifications) {
        return isManualBreachNotificationsEnabled;
      }

      if (item.featureKey === CUSTOMER_FEATURE_KEYS.telelens) {
        return isTeleLensEnabled;
      }

      return true;
    }),
    [isManualBreachNotificationsEnabled, isTeleLensEnabled],
  );
  const handleCopyVersion = async () => {
    try {
      await navigator.clipboard.writeText(formatBuildInfoForClipboard());
      toast({ title: 'Build info copied' });
    } catch (error) {
      toast({
        title: 'Could not copy build info',
        description: 'Clipboard access was unavailable.',
        variant: 'destructive',
      });
    }
  };

  const handleCopyActiveWorkspace = useCallback(async () => {
    if (!activeWorkspaceCopyValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        formatClipboardJson({
          type: 'workspace',
          name: organization?.name ?? null,
          id: organization?.id ?? null,
          value: activeWorkspaceCopyValue,
        }),
      );
      toast({ title: 'Workspace JSON copied to clipboard' });
    } catch (error) {
      toast({
        title: 'Could not copy details',
        description: 'Clipboard access was unavailable.',
        variant: 'destructive',
      });
    }
  }, [activeWorkspaceCopyValue, organization?.id, organization?.name, toast]);

  const handleCopyActiveProfile = useCallback(async () => {
    if (!activeProfileCopyValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        formatClipboardJson({
          type: 'profile',
          name: activeProfileName,
          id: user?.id ?? null,
          email: user?.email ?? null,
          value: activeProfileCopyValue,
        }),
      );
      toast({ title: 'Profile JSON copied to clipboard' });
    } catch (error) {
      toast({
        title: 'Could not copy details',
        description: 'Clipboard access was unavailable.',
        variant: 'destructive',
      });
    }
  }, [activeProfileCopyValue, activeProfileName, organization?.id, organization?.name, toast, user?.email, user?.id]);

  const collapsedNavItemClass = collapsed ? 'mx-auto !size-10 !gap-0 !px-3 !py-2.5 rounded-xl' : '';
  const mainNavContainerClass = collapsed
    ? 'flex-1 min-h-0 overflow-hidden'
    : 'flex-1 min-h-0 overflow-y-auto';
  const bottomSectionClass = collapsed
    ? 'flex-shrink-0 border-t border-purple-500/20 pt-2'
    : 'flex-shrink-0 border-t border-purple-500/20';
  const organizationSwitcherOptions = useMemo(() => {
    type SwitcherOption = {
      id: string;
      name: string;
      badge: string;
      badgeClassName: string;
      helperText: string;
      targetKind: 'direct' | 'managed-customer';
      // ENT-1305: a second badge slot for time-bounded escalated access.
      breakglass?: { expiresAt: string | null } | null;
    };
    const options = new Map<string, SwitcherOption>();

    if (mspOrg) {
      options.set(mspOrg.id, {
        id: mspOrg.id,
        name: mspOrg.name,
        badge: 'MSP',
        badgeClassName: 'border-purple-300/70 bg-purple-50 text-purple-700',
        helperText: 'Home MSP profile',
        targetKind: 'direct',
        breakglass: mspOrg.is_breakglass
          ? { expiresAt: mspOrg.breakglass_expires_at ?? null }
          : null,
      });
    }

    if (mspCustomerOrgs.length > 0) {
      [...mspCustomerOrgs]
        .sort((left, right) => left.name.localeCompare(right.name))
        .forEach((org) => {
          options.set(org.id, {
            id: org.id,
            name: org.name,
            badge: 'Managed',
            badgeClassName: 'border-blue-300/70 bg-blue-50 text-blue-700',
            helperText: 'Managed customer profile',
            targetKind: 'managed-customer',
            breakglass: null,
          });
        });
    }

    [...organizations]
      .sort((left, right) => left.name.localeCompare(right.name))
      .forEach((org) => {
        if (!options.has(org.id)) {
          options.set(org.id, {
            id: org.id,
            name: org.name,
            badge: SURFACE_PROFILE_LABEL,
            badgeClassName: 'border-slate-300/70 bg-slate-50 text-slate-700',
            helperText: 'Direct organisation profile',
            targetKind: 'direct',
            breakglass: org.is_breakglass
              ? { expiresAt: org.breakglass_expires_at ?? null }
              : null,
          });
        }
      });

    return Array.from(options.values());
  }, [mspCustomerOrgs, mspOrg, organizations]);
  const normalizedOrganizationSearch = organizationSearchQuery.trim().toLowerCase();
  const filteredOrganizationSwitcherOptions = useMemo(
    () =>
      organizationSwitcherOptions.filter((option) =>
        normalizedOrganizationSearch.length === 0
          ? true
          : `${option.name} ${option.badge} ${option.helperText}`.toLowerCase().includes(normalizedOrganizationSearch),
      ),
    [normalizedOrganizationSearch, organizationSwitcherOptions],
  );
  const totalOrganizationPages = Math.max(
    1,
    Math.ceil(filteredOrganizationSwitcherOptions.length / ORGANIZATION_SWITCHER_PAGE_SIZE),
  );
  const safeOrganizationPage = Math.min(organizationPage, totalOrganizationPages);
  const organizationPageStart = (safeOrganizationPage - 1) * ORGANIZATION_SWITCHER_PAGE_SIZE;
  const pagedOrganizationSwitcherOptions = filteredOrganizationSwitcherOptions.slice(
    organizationPageStart,
    organizationPageStart + ORGANIZATION_SWITCHER_PAGE_SIZE,
  );
  const handleOrganizationSwitcherOpenChange = (open: boolean) => {
    setOrganizationSwitcherOpen(open);
    if (open) {
      setOrganizationSearchQuery('');
      setOrganizationPage(1);
    }
  };
  const handleSelectOrganization = async (orgId: string) => {
    setOrganizationSwitcherOpen(false);
    setOrganizationSearchQuery('');
    setOrganizationPage(1);

    if (orgId === organization?.id) {
      return;
    }

    await switchOrganizationContext({
      targetOrgId: orgId,
      source: 'organization-switcher',
      targetKind: organizationSwitcherOptions.find((option) => option.id === orgId)?.targetKind,
    });
  };

  return (
    <Sidebar 
      collapsible="icon" 
      className="border-r border-purple-700/30 !bg-gradient-to-b !from-purple-900 !via-purple-800 !to-violet-900 [&_[data-sidebar=sidebar]]:relative [&_[data-sidebar=sidebar]]:overflow-hidden [&_[data-sidebar=sidebar]]:bg-gradient-to-b [&_[data-sidebar=sidebar]]:from-purple-900 [&_[data-sidebar=sidebar]]:via-purple-800 [&_[data-sidebar=sidebar]]:to-violet-900"
    >
      <SurfaceSidebarBackdrop tone="workspace" collapsed={collapsed} idPrefix="sidebar-depth" testId="sidebar-depth-layer" />

      <SurfaceSidebarHeader
        tone="workspace"
        commandPalette={(
          <AppCommandPalette
            collapsed={collapsed}
            items={[...contextualCommandPaletteItems, ...commandPaletteItems]}
            organizationId={organization?.id}
            isMSP={isMSP}
            tone="workspace"
            onOpenCustomerView={switchOrganizationContext}
          />
        )}
      >
        <Dialog open={organizationSwitcherOpen} onOpenChange={handleOrganizationSwitcherOpenChange}>
          <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
            {!collapsed ? (
              <SurfaceSidebarBrandButton
                tone="workspace"
                // ENT-3705: organisation settings are admin-only and fail closed. Only advertise
                // and route to them for a manager; a non-admin's logo click opens their own
                // Personal Settings rather than bouncing off the org-settings guard.
                ariaLabel={canManageOrgSettings ? 'Open organisation settings' : 'Open personal settings'}
                onClick={() => navigate(canManageOrgSettings ? '/settings/organisation?tab=organization' : '/settings')}
              >
                <SurfaceSidebarBrandPanel tone="workspace">
                  <div
                    data-testid="workspace-mark"
                    data-state={workspaceMarkState}
                    aria-busy={workspaceMarkState === 'loading' || undefined}
                    className="flex h-full w-full items-center justify-center"
                  >
                    {workspaceMarkState === 'logo' ? (
                      <SafeImageFallback
                        src={activeOrganizationImageSrc}
                        alt={`${organization?.name ?? 'Organization'} logo`}
                        className="h-full w-full rounded-lg border border-slate-300 object-cover"
                        fallbackClassName="h-6 w-6 text-purple-100"
                      />
                    ) : workspaceMarkState === 'initials' ? (
                      <span
                        aria-label={`${organization?.name ?? 'Organization'} initials`}
                        className="text-sm font-semibold text-purple-100"
                      >
                        {workspaceInitials}
                      </span>
                    ) : workspaceMarkState === 'error' ? (
                      <span
                        role="img"
                        aria-label="Workspace logo unavailable"
                        title="Workspace logo unavailable — reload to try again"
                        className="flex h-full w-full items-center justify-center"
                      >
                        <AlertTriangle className="h-5 w-5 text-amber-200" aria-hidden="true" />
                      </span>
                    ) : (
                      <span aria-hidden="true" className="h-full w-full animate-pulse rounded-lg bg-white/10" />
                    )}
                  </div>
                </SurfaceSidebarBrandPanel>
              </SurfaceSidebarBrandButton>
            ) : null}
            <SurfaceSidebarSwitcherControl
              collapsed={collapsed}
            >
              <DialogTrigger asChild>
                <SurfaceSidebarSwitcherButton
                  tone="workspace"
                  collapsed={collapsed}
                  ariaLabel="Open organisation switcher"
                  title={isMSP ? MSP_PROFILE_LABEL : CUSTOMER_PROFILE_LABEL}
                  subtitle={organization?.name || 'Select Org'}
                  trailingAccessory={!collapsed && activeWorkspaceCopyValue ? (
                    <SurfaceSidebarCopyButton
                      tone="workspace"
                      ariaLabel="Copy active workspace email"
                      onClick={() => void handleCopyActiveWorkspace()}
                      className="shrink-0"
                    />
                  ) : undefined}
                />
              </DialogTrigger>
            </SurfaceSidebarSwitcherControl>
          </div>
          <DialogContent className="max-w-2xl p-0">
            <DialogHeader className="border-b px-6 py-5">
              <DialogTitle>Switch organisation</DialogTitle>
              <DialogDescription>
                Search and page through MSP and customer workspaces from one standard organisation switcher.
              </DialogDescription>
            </DialogHeader>
            <div className="flex max-h-[70vh] flex-col gap-4 p-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={organizationSearchQuery}
                  onChange={(event) => {
                    setOrganizationSearchQuery(event.target.value);
                    setOrganizationPage(1);
                  }}
                  placeholder="Search organisations..."
                  className="pl-9"
                />
              </div>
              <div className="space-y-2 overflow-y-auto">
                {pagedOrganizationSwitcherOptions.length > 0 ? (
                  pagedOrganizationSwitcherOptions.map((option) => {
                    const isActiveOption = option.id === organization?.id;

                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => void handleSelectOrganization(option.id)}
                        className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition ${isActiveOption ? 'border-primary bg-primary/5 shadow-sm' : 'border-border hover:border-primary/40 hover:bg-muted/50'}`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-medium text-foreground">{option.name}</p>
                            <Badge variant="outline" className={option.badgeClassName}>
                              {option.badge}
                            </Badge>
                            {option.breakglass ? (
                              <Badge
                                variant="outline"
                                className="border-red-300/70 bg-red-50 text-red-700"
                                title={
                                  option.breakglass.expiresAt
                                    ? `Break-glass access — expires ${new Date(option.breakglass.expiresAt).toLocaleString()}`
                                    : 'Break-glass access — time-bounded escalated session'
                                }
                                data-testid={`org-switcher-breakglass-badge-${option.id}`}
                              >
                                Break-glass
                              </Badge>
                            ) : null}
                            {isActiveOption ? (
                              <Badge variant="outline" className="border-emerald-300/70 bg-emerald-50 text-emerald-700">
                                Current
                              </Badge>
                            ) : null}
                          </div>
                          <p className="truncate text-xs text-muted-foreground">
                            {option.breakglass
                              ? 'Active break-glass session — time-bounded escalated access'
                              : option.helperText}
                          </p>
                        </div>
                        <ChevronDown className="h-4 w-4 rotate-[-90deg] text-muted-foreground" />
                      </button>
                    );
                  })
                ) : (
                  <div className="rounded-xl border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                    No organisations match that search.
                  </div>
                )}
              </div>
              <PaginationControls
                currentPage={safeOrganizationPage}
                totalPages={totalOrganizationPages}
                totalItems={filteredOrganizationSwitcherOptions.length}
                pageSize={ORGANIZATION_SWITCHER_PAGE_SIZE}
                startIndex={organizationPageStart}
                endIndex={Math.min(
                  organizationPageStart + pagedOrganizationSwitcherOptions.length,
                  filteredOrganizationSwitcherOptions.length,
                )}
                hasNextPage={safeOrganizationPage < totalOrganizationPages}
                hasPreviousPage={safeOrganizationPage > 1}
                onNextPage={() => setOrganizationPage((current) => Math.min(current + 1, totalOrganizationPages))}
                onPreviousPage={() => setOrganizationPage((current) => Math.max(current - 1, 1))}
                onGoToPage={(page) => setOrganizationPage(page)}
                showPageSizeSelector={false}
                itemLabel="organisations"
              />
            </div>
          </DialogContent>
        </Dialog>
      </SurfaceSidebarHeader>

      <SurfaceSidebarContent>
        {/* Main Navigation - Scrollable */}
        <div data-testid="sidebar-main-nav" className={mainNavContainerClass}>
          {/* Main nav items - hidden for customer_service role */}
          {!isCustomerService && (
          <SurfaceSidebarSection tone="workspace">
              <SidebarMenu>
                {visibleMainNavItems.map((item) => {
                  const prefetchHandler = getPrefetchHandler(item.url);
                  const showBreachAcknowledgementCount = item.url === '/breach-notifications'
                    && breachAcknowledgementCount > 0;
                  const acknowledgementLabel = `${breachAcknowledgementCount} breach ${breachAcknowledgementCount === 1 ? 'acknowledgement' : 'acknowledgements'} required`;
                  return (
                    <SurfaceSidebarNavItemLink
                      key={item.title}
                      tone="workspace"
                      to={item.url}
                      title={item.title}
                      icon={item.icon}
                      collapsed={collapsed}
                      collapsedNavItemClass={collapsedNavItemClass}
                      onMouseEnter={prefetchHandler}
                      onFocus={prefetchHandler}
                      trailingAccessory={showBreachAcknowledgementCount ? (
                        <Badge
                          variant="destructive"
                          aria-label={acknowledgementLabel}
                          className="h-5 min-w-5 justify-center rounded-full border-0 px-1.5 text-[11px] font-semibold tabular-nums"
                        >
                          {breachAcknowledgementCount > 99 ? '99+' : breachAcknowledgementCount}
                        </Badge>
                      ) : undefined}
                      collapsedAccessory={showBreachAcknowledgementCount ? (
                        <span
                          aria-label={acknowledgementLabel}
                          className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold leading-none text-destructive-foreground tabular-nums"
                        >
                          {breachAcknowledgementCount > 9 ? '9+' : breachAcknowledgementCount}
                        </span>
                      ) : undefined}
                    />
                  );
                })}
                {/* ENT-5889 (v1.12): flag-gated, hidden while loading; per-user unread badge. */}
                <AdvisoriesNavItem
                  audience="customer"
                  tone="workspace"
                  collapsed={collapsed}
                  collapsedNavItemClass={collapsedNavItemClass}
                />
              </SidebarMenu>
          </SurfaceSidebarSection>
          )}

          {/* ENT-6091: ratified supplier-only advisory exception on the CS own organisation. */}
          {isCustomerService && (
            <SurfaceSidebarSection tone="workspace" label="Organisation">
              <SidebarMenu>
                <AdvisoriesNavItem audience="customer" tone="workspace" collapsed={collapsed} collapsedNavItemClass={collapsedNavItemClass} />
              </SidebarMenu>
            </SurfaceSidebarSection>
          )}

          {/* MSP Navigation - Only visible for MSP accounts */}
          {isMSP && (
            <SurfaceSidebarSection tone="workspace" label="MSP">
                <SidebarMenu>
                  {(isCustomerService ? visibleMspNavItems.filter(i => customerServiceMspNavUrls.has(i.url)) : visibleMspNavItems).map((item) => (
                    <SurfaceSidebarNavItemLink
                      key={item.title}
                      tone="workspace"
                      to={item.url}
                      title={item.title}
                      icon={item.icon}
                      collapsed={collapsed}
                      collapsedNavItemClass={collapsedNavItemClass}
                    />
                  ))}
                  <AdvisoriesNavItem
                    audience="msp"
                    tone="workspace"
                    collapsed={collapsed}
                    collapsedNavItemClass={collapsedNavItemClass}
                  />
                </SidebarMenu>
            </SurfaceSidebarSection>
          )}

          {adminNavItems.length > 0 && (
            <SurfaceSidebarSection tone="workspace" label="Admin">
                <SidebarMenu>
                  {adminNavItems.map((item) => (
                    <SurfaceSidebarNavItemLink
                      key={item.title}
                      tone="workspace"
                      to={item.url}
                      title={item.title}
                      icon={item.icon}
                      collapsed={collapsed}
                      collapsedNavItemClass={collapsedNavItemClass}
                    />
                  ))}
                </SidebarMenu>
            </SurfaceSidebarSection>
          )}
        </div>

        {/* Bottom Fixed Section */}
        <div data-testid="sidebar-bottom-section" className={bottomSectionClass}>
          {/* Supplier Portal Link - Bottom - Hidden for customer_service role */}
          {!isCustomerService && (() => {
            const shouldShowPortalSection = Boolean(
              (hasSupplierPortal && supplierPortalUrl) || isSuperAdminSurfaceEnabled,
            );

            if (shouldShowPortalSection) {
              return (
                <SurfaceSidebarSection tone="workspace" label={SWITCH_PROFILE_LABEL} className="py-2">
                    <SidebarMenu>
                      {hasSupplierPortal && supplierPortalUrl ? (
                        <SurfaceSidebarPortalLink
                          tone="workspace"
                          to={supplierPortalUrl}
                          title={supplierPortalLabel}
                          icon={ArrowLeftRight}
                          collapsed={collapsed}
                          collapsedNavItemClass={collapsedNavItemClass}
                          onClick={handleSupplierPortalClick}
                          accent="emerald"
                        />
                      ) : null}
                      {isSuperAdminSurfaceEnabled ? (
                        <SurfaceSidebarPortalLink
                          tone="workspace"
                          to={adminDashboardUrl}
                          title={ADMIN_PROFILE_LABEL}
                          icon={Settings}
                          collapsed={collapsed}
                          collapsedNavItemClass={collapsedNavItemClass}
                          onClick={() => beginPortalSwitch('admin')}
                          accent="rose"
                        />
                      ) : null}
                    </SidebarMenu>
                </SurfaceSidebarSection>
              );
            }
            return null;
          })()}
        </div>
      </SurfaceSidebarContent>

      <SurfaceSidebarFooter tone="workspace" bordered={!collapsed}>
        <SurfaceSidebarProfileMenu
          tone="workspace"
          collapsed={collapsed}
          avatarLabel="Profile picture"
          avatarSrc={resolvedProfile?.avatar_url || undefined}
          initials={userInitials}
          name={activeProfileName}
          roleLabel={membership?.role || 'Member'}
          onAvatarClick={() => navigate('/settings?tab=profile')}
          avatarAriaLabel="Open Profile Settings"
          copyButton={!collapsed && activeProfileCopyValue ? {
            ariaLabel: 'Copy active profile email',
            onClick: () => void handleCopyActiveProfile(),
          } : undefined}
        >
            <ThemeModeMenu />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="h-4 w-4 mr-2" />
              Personal Settings
              <DropdownMenuShortcut>{settingsShortcutHint}</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleCopyVersion}>
              <Copy className="h-4 w-4 mr-2" />
              <div className="flex min-w-0 flex-col">
                <span className="whitespace-nowrap text-[11px] leading-none">{appVersionLabel}</span>
                <span className="whitespace-nowrap text-[10px] leading-tight text-muted-foreground">
                  {builtAtLabel}
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
        </SurfaceSidebarProfileMenu>
      </SurfaceSidebarFooter>
    </Sidebar>
  );
}
